import { html, LitElement } from 'lit';
import { customElement, state, property, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { WEB_COMPONENT_POLICY_PANEL_ACCORDION_NAVIGATION_HEADER } from '../../../../../../webcomponents.constants';
import { connect } from 'pwa-helpers';
import { RootState, store } from '@nirvana/redux/store';
import { selectPoliciesState } from '../../../../../endpoints/policyPanel/redux/slice';
import { PolicyAppState } from '../../../../../endpoints/policyPanel/redux/types';
import { getNavigationHeaderSlice } from '../../../../../endpoints/policyPanel/redux/navigationHeaderSlice/actions.navigation';
import { PolicyContext } from '../../../../../endpoints/policyPanel/redux/navigationHeaderSlice/types.navigation';
import { ModuleId, SectionId } from '@nirvana/shared/dtos/types/policies/common';
import { getTruncatedText, PageNavigationHeaderItem } from '../../../../../../ui/gzPageNavigationHeader/types';
import {
  getLabelForPolicyContext,
  getModuleLabelById,
  getSectionLabelById,
} from '../../../../../endpoints/policyPanel/searchableComponents/searchablePolicyPanel/utils/treeItems';
import { FontWeights } from '../../../../../../types/common/types';
import { GzPageNavigationHeaderWebComponent } from '../../../../../../ui/gzPageNavigationHeader';

@customElement(WEB_COMPONENT_POLICY_PANEL_ACCORDION_NAVIGATION_HEADER)
export class GzPolicyPanelAccordionNavigationHeaderWebComponent extends connect(store)(LitElement) {
  /**
   * Property for module id.
   */
  @property({ type: String, attribute: true, reflect: true })
  moduleId: ModuleId;

  /**
   * Property for section id.
   */
  @property({ type: String, attribute: true, reflect: true })
  sectionId: SectionId;

  /**
   * State used to store policy name. It is used to build header items.
   */
  @state()
  policyName = '';

  /**
   * State used to store policy context. It is used to build header items.
   */
  @state()
  context: PolicyContext = PolicyContext.ADD;

  // header component
  @query('gz-page-navigation-header')
  private pageNavigationHeader: GzPageNavigationHeaderWebComponent;

  static get styles() {
    return styles;
  }

  /**
   * When store state changed and navigation header slice is updated, update header items.
   * @param rootState
   */
  public stateChanged(rootState: RootState) {
    const appState = selectPoliciesState(rootState);
    const navigationHeaderSlice = getNavigationHeaderSlice(appState);

    if (!navigationHeaderSlice.updateNeeded) {
      return;
    }

    this.policyName = navigationHeaderSlice.policyName;
    this.context = navigationHeaderSlice.context;
  }

  /**
   * Render component.
   */
  protected render() {
    this.updateComplete.then(() => {
      this.buildHeader();
    });

    return html` <gz-page-navigation-header></gz-page-navigation-header> `;
  }

  /**
   * Build header items for accordion (path to current section).
   */
  private buildHeader() {
    const items: PageNavigationHeaderItem[] = [
      {
        text: '[' + getLabelForPolicyContext(this.context) + '] ' + getTruncatedText(this.policyName),
      },
    ];

    if (this.moduleId) {
      items.push({
        text: getModuleLabelById(this.moduleId),
      });
    }

    // add section only if it is available
    if (this.sectionId !== SectionId.NONE) {
      items.push({
        text: getSectionLabelById(this.sectionId, this.moduleId),
      });
    }

    // set medium font weight for last item
    items[items.length - 1].fontWeight = FontWeights.MEDIUM;
    this.pageNavigationHeader.items = items;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_ACCORDION_NAVIGATION_HEADER: GzPolicyPanelAccordionNavigationHeaderWebComponent;
  }
}
